import { z } from 'zod'
import { SubscriptionPlanSchema } from './Validation'
import { validateHexacode } from '~/utils/backend/devices-api'

export const DeviceHexacodeSchema = z.string().regex(/[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}\.[A-F0-9a-f]{2}/).length(17)

export const DeviceRegistrationStatus = z.union([
  z.literal('REGISTERED'),
  z.literal('CLAIMED'),
])

export const DeviceBufferDataStorageTypeSchema = z.union([
  z.literal('UTC'),
  z.literal('LOCAL_TIME'),
])

export const DeviceStatusSchema = z.union([
  z.literal('ON'),
  z.literal('OFF'),
  z.literal('ON_ALARM'),
  z.literal('OFF_ALARM'),
])
export const DeviceTagLevelSchema = z.union([
  z.literal('INFO'),
  z.literal('WARN'),
  z.literal('ERROR'),
])
export const DeviceTagSchema = z.object({
  level: DeviceTagLevelSchema,
  message: z.string(),
})
export const DeviceTypeSchema = z.union([z.literal('VIRTUAL_DEVICE'), z.literal('BUSINESS_GROUP'), z.literal('REAL_DEVICE')]) // z.literal('DEPARTMENT')
export const AddressingTypeSchema = z.union([z.literal('OLD'), z.literal('NEW')])
export const ProtocolVersionSchema = z.union([z.literal('Optilink70'), z.literal('Optilink71')])

export const CacheGroupSettingsSchema = z.object({
  id: z.string(),
  name: z.string(),
  opCodes: z.string().array(),
  cacheDuration: z.number(),
})

export const VirtualMappingInformationSchema = z.object({
  id: z.string().optional(),
  type: DeviceTypeSchema,
  virtualHexacode: z.string(),
  rawVirtualHexacode: z.number().nullable(),
  mappingHexacode: z.string({ message: 'Device is required' }).min(1, { message: 'Device is required' }),
  virtualMappingName: z.string(),
  status: DeviceStatusSchema,
  empty: z.boolean(),
  departmentNotPresent: z.boolean(),
  rawMappingHexcode: z.number().nullable(),
  mappingName: z.string(),
  realHexacode: z.string(),
  rawRealHexacode: z.number().nullable(),
  networkController: z.boolean(),
  department: z.number(),
  subDepartment: z.number(),
  departmentStatus: DeviceStatusSchema,
  offlineSince: z.string().nullable(),

  networkNumber: z.number({ message: 'Network number is required' }).max(99).min(0),
  mappingNetworkNumber: z.number({ message: 'Virtual department is required' }).max(99).min(0),
  departmentName: z.string({ message: 'Department name is required' }).min(1, { message: 'Department name is required' }),
})

export const MappingInformationSchema = VirtualMappingInformationSchema.extend({
  mappingNetworkNumber: z.number().optional(),
  departmentName: z.string().optional(),
})
export const DeviceSettingsSchemaBase = z.object({
  maxConnections: z.number({ message: 'Max connections is required' }).min(0),
})

const BaseSettingsFields = {
  sendOfflineAlarm: z.boolean(),
  alarmInterval: z.number({ message: 'Offline alarm interval is required' }).min(0),
  pingInterval: z.number().min(0),
  buffersCheckInterval: z.number().min(0),
  buffersCheckRetryInterval: z.number().min(0, { message: 'Buffer collection retry is required' }),
  sendOfflineAlarmInterval: z.number().min(0, { message: 'Offline alarm interval is required' }),
  buffersValidity: z.number({ message: 'Buffers validity is required' }).min(0),
  maxBufferSamples: z.number({ message: 'Max samples per buffer is required' }).min(0),
  maxSamplePoint: z.number({ message: 'Max samples per day is required' }).min(0),
  cacheEnabled: z.boolean(),
  cacheGroupSettings: CacheGroupSettingsSchema.array(),
  maxConnections: z.number({ message: 'Max connections is required' }).min(0),
}

export const DeviceSettingsSchema = z.object(BaseSettingsFields)

export type DeviceSettings = z.infer<typeof DeviceSettingsSchema>

export const BaseDeviceSchema = z.object({
  id: z.string().nullable(),
  name: z.string({ message: 'Name is required' }).trim().min(1, { message: 'Name is required' }),
  hexacode: z.string({ message: 'Hexacode is required' }).min(1, { message: 'Hexacode is required' }).length(17, 'Hexacode is not valid'),
  optimaBoxDevice: z.boolean().default(false),
  demoDevice: z.boolean().default(false),
  customer: z.string().nullable().optional(),
  dealer: z.string().nullable().optional(),
  subDealer: z.string().nullable().optional(),
  licenseCode: z.string().optional(),
  redirectAddress: z.string().nullable().optional(),
  subscriptionPlans: SubscriptionPlanSchema.array().default([]),
  ubn: z.number().nullable().optional(),
  createDate: z.string().nullable().optional(),
  additionalInformation: z.record(z.string(), z.string()).optional(),
  host: z.string().optional(),
  versionNumber: z.string().optional(),
  revisionNumber: z.string().optional(),
  addressingType: AddressingTypeSchema.optional(),
  supportsXTI: z.boolean().optional(),
  supportsAX: z.boolean().optional(),
  supportsXfs: z.boolean().optional(),
  supportsXfsLogs: z.boolean().optional(),
  supportsXfsUpdates: z.boolean().optional(),
  bufferDataStorageTimeType: DeviceBufferDataStorageTypeSchema.optional().default('LOCAL_TIME'),
  lastKnownTimezone: z.string().optional(),
  ipAddress: z.string().optional(),
  ccId: z.string().optional(),
  firstConnection: z.string().optional(),
  connectedSince: z.string().optional(),
  offlineSince: z.string().optional(),
  status: DeviceStatusSchema.default('OFF'),
})

export const BussinessGroup = BaseDeviceSchema.extend({
  type: z.literal('BUSINESS_GROUP'),
  mappings: MappingInformationSchema.array().default([]),
})

export const VirtualDevice = BaseDeviceSchema.extend({
  type: z.literal('VIRTUAL_DEVICE'),
  mappings: VirtualMappingInformationSchema.array().default([]),
})
export const RealDevice = BaseDeviceSchema.extend({
  type: z.literal('REAL_DEVICE'),
  settingsTemplate: z.string().optional().nullable(),
  settings: DeviceSettingsSchema,
  mappings: VirtualMappingInformationSchema.array().default([]),
  registrationStatus: DeviceRegistrationStatus.default('CLAIMED'),
})

export const DeviceSchema = z.discriminatedUnion('type', [BussinessGroup, VirtualDevice, RealDevice])
  .superRefine(async (arg, ctx) => {
    const isValid = await validateHexacode(arg.id, arg.hexacode).then(r => r.data).catch(() => false)
    if (!isValid) {
      ctx.addIssue({
        path: ['hexacode'],
        message: 'Hexacode already exists',
        code: z.ZodIssueCode.custom,
      })
    }
  })

export const DeviceCreateRequestSchema = z.object({
  device: DeviceSchema,
  linkedGroups: z.string().array(),
})
